// Порт ref DesignBlockEditor (корневая часть): сортируемый список элементов,
// пустые состояния, предупреждение о переполнении макета.
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { useEffect, useState } from 'react'
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { blockTheme, cn, hsl } from '@teach/ui'
import type { DesignElement, DesignElementType } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import {
  DESIGN_LIMITS,
  createDesignElement,
  moveElement,
  removeElement,
  updateElementAt,
} from '@/features/editor/lib/designElements'
import { useBlockDraftContext } from '@/features/editor/components/studio/BlockDraftContext'
import { usePhoneScale } from '../phoneScale'
import { DesignElementView } from './DesignElementView'
import { DesignEmptyState } from './DesignEmptyState'
import { DesignGhostElement } from './DesignGhostElement'
import { DesignAddElement } from './DesignAddElement'
import { useIsPreviewThumbnail } from '../thumbnailContext'
import { DesignQuickToolbar } from './DesignQuickToolbar'
import { getLocalizedField } from '@/features/editor/lib/localizedField'
import { QuickToolbarContext } from './quickToolbarContext'
import { SortableDesignElement } from './SortableDesignElement'
import { createScaleModifier } from './scaleModifier'
import { createScaleCollision } from './scaleCollision'
import { createScaledStrategy } from './scaleSortingStrategy'
import { useOverflowDetection } from './useOverflowDetection'
import { setDesignOverflow } from '@/features/editor/lib/designOverflow'

interface Props {
  elements: DesignElement[]
  editable: boolean
  contentLang: ContentLang
  onChange: (next: DesignElement[]) => void
  /** Прохождение урока: пустой блок и элементы рисуют то, что увидит читатель
   *  (пусто), а не редакторские заглушки. */
  isRun?: boolean
}

export const DesignCanvas = ({ elements, editable, contentLang, onChange, isRun }: Props) => {
  const m = useMessages(messages)
  const { selectedElementId, setSelectedElementId } = useBlockDraftContext()
  const isThumbnail = useIsPreviewThumbnail()
  const scale = usePhoneScale()
  const [preview, setPreview] = useState<{ id: string; patch: Partial<DesignElement> } | null>(null)
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [adding, setAdding] = useState(false)
  // Only WYSIWYG instances need the overflow warning — a read-only thumbnail
  // pays for a ResizeObserver/MutationObserver pair it never reads.
  const overflowLevel = useOverflowDetection(editable ? node : null)
  // Значок «переполнен» живёт в панели кнопок под холстом (`DesignOverflowWarning`):
  // плашка поверх экрана перекрывала содержимое.
  useEffect(() => {
    if (!editable) return
    setDesignOverflow(overflowLevel)
    return () => setDesignOverflow('fits')
  }, [editable, overflowLevel])
  // Без KeyboardSensor: в рефе он перехватывал пробел при наборе текста в элементе.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // Добавить элемент в конец и сразу выделить — с холста («+» и пустое состояние).
  const addElement = (type: DesignElementType) => {
    // Подставной текст — на языке правки (на английском — в слот `*En`).
    const element = createDesignElement(type)
    onChange([...elements, element])
    setSelectedElementId(element.id)
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const from = elements.findIndex((el) => el.id === active.id)
    const to = elements.findIndex((el) => el.id === over.id)
    onChange(moveElement(elements, from, to))
  }

  if (elements.length === 0) {
    // Прохождение: плеер академии на пустом списке элементов не рисует ничего —
    // и «Пустой составной блок», и «Нет контента» ниже авторские подсказки формы.
    // Миниатюра в ленте экранов тоже без подписи «Нет контента» — пустой экран
    // и так виден, а надпись на крошечной карточке только шумит.
    if (isRun || isThumbnail) return null
    // w-full обязателен: контейнер превью — flex-строка, без ширины пустое
    // состояние сжимается по содержимому и прилипает к левому краю.
    return editable ? (
      <DesignEmptyState contentLang={contentLang} onAdd={addElement} />
    ) : (
      <div className="flex h-full w-full items-center justify-center p-6">
        <p className="text-sm" style={{ color: hsl(blockTheme.foreground, 0.5) }}>
          {m.noContent}
        </p>
      </div>
    )
  }

  return (
    <div
      ref={setNode}
      data-design-canvas
      onClick={() => (editable ? setSelectedElementId(null) : undefined)}
      className={cn(
        // Без полосы прокрутки: в Windows она занимает место — появлялась вместе с
        // высокой примеркой, сужала холст, ряд «+» переносился по-другому, курсор
        // слетал с типа, примерка пропадала, полоса тоже — и так по кругу (дёрганье).
        // Поле по бокам 20 — как у раннера мобилки: превью переносит строки так же,
        // как телефон (раньше было 16 + 16 бокового отступа элемента ≈ 32).
        'relative flex h-full w-full flex-col overflow-auto px-5 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        editable && overflowLevel === 'warn' ? 'ring-2 ring-inset ring-warning' : null,
        editable && overflowLevel === 'over' ? 'ring-2 ring-inset ring-destructive' : null,
      )}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={createScaleCollision(scale)}
        modifiers={[createScaleModifier(scale)]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={elements.map((el) => el.id)}
          strategy={createScaledStrategy(scale, verticalListSortingStrategy)}
        >
          {/* leading-[normal], не leading-normal: у Tailwind `leading-normal` — это
              line-height 1.5, само слово «normal» в имени класса не про CSS-ключевое
              слово `normal`, это ловушка в названии. `leading-[normal]` — произвольное
              значение, компилируется буквально в `line-height: normal`, как требует
              макет. Read-only текст (RichTextView) своего класса межстрочного
              интервала не имеет и берёт это значение наследованием отсюда — тег
              h1..h6 в index.css такого правила не ставит, поэтому наследование тут
              срабатывает. Editable-ветка (RichTextEditor) держит СВОЙ явный
              leading-[normal] прямо на ProseMirror-элементе — там раньше стоял
              leading-tight, а прямое правило на элементе побеждает наследование
              с корня независимо от специфичности, поэтому только замена значения
              (не удаление класса) свела обе ветки к одной раскладке. */}
          <div className="flex w-full flex-col space-y-1 font-madefor leading-[normal]">
            {elements.map((source, index) => {
              // Примерка значения из списка быстрой панели — только показ, не черновик.
              const element =
                preview?.id === source.id
                  ? ({ ...source, ...preview.patch } as DesignElement)
                  : source
              return (
                <SortableDesignElement
                  key={element.id}
                  element={element}
                  editable={editable}
                  isSelected={selectedElementId === element.id}
                  onSelect={() => setSelectedElementId(element.id)}
                  onDelete={() => onChange(removeElement(elements, element.id))}
                  toolbarBelow={index === 0}
                  toolbar={
                    // Пустой картинке наклонять нечего — панели над ней нет вовсе.
                    source.type === 'image' &&
                    !getLocalizedField(source, 'imageUrl', contentLang) ? undefined : (
                      <QuickToolbarContext.Provider
                        value={{
                          below: index === 0,
                          preview: (patch) => setPreview(patch ? { id: source.id, patch } : null),
                        }}
                      >
                        <DesignQuickToolbar
                          element={source}
                          onUpdate={(patch) =>
                            onChange(updateElementAt(elements, source.id, patch))
                          }
                        />
                      </QuickToolbarContext.Provider>
                    )
                  }
                >
                  <DesignElementView
                    element={element}
                    editable={editable}
                    contentLang={contentLang}
                    isRun={isRun}
                    onSelect={() => setSelectedElementId(element.id)}
                    onUpdate={(patch) => onChange(updateElementAt(elements, element.id, patch))}
                  />
                </SortableDesignElement>
              )
            })}
            {/* Примерка — в конце списка, где появится элемент, над рядом «+». */}
            {editable ? <DesignGhostElement contentLang={contentLang} /> : null}
          </div>
        </SortableContext>
      </DndContext>
      {/* «+» — строка под последним элементом: пока элементов мало, стоит внизу
          (mt-auto). В покое НЕ sticky: прилипший к низу, на длинном блоке он ложился
          поверх нижнего элемента. Пока раскрыт выбор типа — sticky: примерка в конце
          списка иначе сдвигала строку из-под курсора, наведение слетало, примерка
          пропадала, строка возвращалась — и так по кругу (мигание). */}
      {editable && elements.length < DESIGN_LIMITS.ELEMENTS ? (
        <div
          data-design-chrome
          className={cn('z-20 mt-auto pt-1 pb-1', adding ? 'sticky bottom-0' : 'relative')}
        >
          <DesignAddElement
            onAdd={addElement}
            // Открыли «+» — выделение с элементов снимаем: новый встанет вниз,
            // а панель настроек и тулбар старого только мешали бы выбору типа.
            onOpenChange={(open) => {
              setAdding(open)
              if (open) setSelectedElementId(null)
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
